import { useState } from 'react';
import { EventContentArg, EventClickArg } from '@fullcalendar/core';
import { toast } from 'react-toastify';
import {
  useSchedule,
  useMyRecoveries,
  useMyLevels,
  useCancelClass,
  useRescheduleClass,
  useJoinWaitlist,
  useLeaveWaitlist,
  ScheduleEvent,
  ClientLevel,
} from '../../services/hooks/useClientPortal';
import BaseCalendar from '../calendario/BaseCalendar';
import ClientClassDetailModal, { ClassVariant } from './ClientClassDetailModal';
import MobileDayAgenda from './MobileDayAgenda';
import { useIsMobile } from '../../services/hooks/useIsMobile';

// Color encodes enrollment STATE, not the class level. Soft pastel backgrounds
// with a darker matching text color so events stay readable.
// green = enrolled, blue = bookable make-up, red = full, gray = everything else.
//
// El rojo es solo para "completa" — una clase sin sitio. El resto de clases no
// reservables se quedan en gris: no tienen nada de malo, simplemente no son
// para este cliente, y pintarlas también de rojo diluiría el aviso.
const variantColor = (variant: ClassVariant): string =>
  variant === 'enrolled' ? '#bbf7d0'
    : variant === 'bookable' ? '#bfdbfe'
    : variant === 'full' ? '#fecaca'
    : '#e5e7eb';

const variantTextColor = (variant: ClassVariant): string =>
  variant === 'enrolled' ? '#166534'
    : variant === 'bookable' ? '#1e40af'
    : variant === 'full' ? '#991b1b'
    : '#4b5563';

// D1 — a full class the client is queued for. Amber: waiting, not blocked.
const WAITLIST_BG = '#fde68a';
const WAITLIST_TEXT = '#92400e';

// P5 — un festivo. Gris, y NO el salmón que usa el calendario del centro
// (`MainCalendar`): aquí convive con el rojo de "Completa", y un día entero en
// rojizo se lee como una alarma — como si pasara algo malo— cuando lo único que
// dice es que el centro no abre. El gris es justo eso: un día apagado.
const HOLIDAY_BG = '#cbd5e1';
const HOLIDAY_TEXT = '#334155';

type PopulatedRef = { _id?: string; nombre?: string; color?: string } | string | undefined;

const refName = (ref: PopulatedRef): string =>
  (typeof ref === 'object' && ref?.nombre ? ref.nombre : typeof ref === 'string' ? ref : '');

const refId = (ref: PopulatedRef): string =>
  (typeof ref === 'object' && ref?._id ? ref._id : typeof ref === 'string' ? ref : '');

// Combine the stored date with the "HH:mm" strings into a local ISO datetime.
const toDateTime = (fecha: string, hora: string): string => `${fecha.split('T')[0]}T${hora}:00`;

// Las políticas del centro NO impiden cancelar: deciden si la cancelación da
// derecho a una recuperación. Cancelar siempre se puede (mientras la clase no
// haya empezado); lo que se pierde al cancelar tarde es la clase de repuesto.
// Debe reflejar la misma regla que `cancelClass` en el backend.
const getCancelInfo = (
  fecha: string,
  horaInicio: string,
  minCancellationHours: number,
  cancellationHorizonDays?: number | null
): { cancelable: boolean; earnsRecovery: boolean; message: string } => {
  const eventDateTime = new Date(toDateTime(fecha, horaInicio));
  const hoursUntil = (eventDateTime.getTime() - Date.now()) / (1000 * 60 * 60);
  if (hoursUntil <= 0) {
    return { cancelable: false, earnsRecovery: false, message: 'La clase ya ha comenzado' };
  }
  if (hoursUntil < minCancellationHours) {
    return {
      cancelable: true,
      earnsRecovery: false,
      message: `Quedan menos de ${minCancellationHours}h para la clase: puedes cancelarla, pero no tendrás recuperación.`,
    };
  }
  if (cancellationHorizonDays && hoursUntil / 24 > cancellationHorizonDays) {
    return {
      cancelable: true,
      earnsRecovery: false,
      message: `Solo dan derecho a recuperación las cancelaciones dentro de los ${cancellationHorizonDays} días previos a la clase.`,
    };
  }
  return { cancelable: true, earnsRecovery: true, message: '' };
};

interface Recovery {
  _id: string;
  levelId: { _id: string } | string;
  /** Último instante en que puede CELEBRARSE la clase de recuperación. */
  rescheduleDeadline: string;
}

const ClientCalendar = () => {
  const isMobile = useIsMobile();
  const { data, isLoading, isError } = useSchedule();
  const { data: recoveriesData } = useMyRecoveries();
  const { data: levelsData } = useMyLevels();
  const cancelClass = useCancelClass();
  const reschedule = useRescheduleClass();
  const joinWaitlist = useJoinWaitlist();
  const leaveWaitlist = useLeaveWaitlist();
  const [selected, setSelected] = useState<{ event: ScheduleEvent; variant: ClassVariant } | null>(null);

  // Rango que el cliente está viendo ahora mismo en la rejilla. Hace falta para
  // distinguir "no tienes clases nunca" de "no tienes clases en ESTE mes": si el
  // aviso mira solo el total de eventos, quien se inscribe en agosto con las
  // clases empezando en septiembre ve agosto en blanco y sin explicación.
  const [vistaRango, setVistaRango] = useState<{ start: Date; end: Date; titulo: string } | null>(null);

  const events = data?.events ?? [];
  const config = data?.config ?? { minCancellationHours: 24, rescheduleDays: 15 };

  // Se acota la NAVEGACIÓN además de los datos: si solo se filtran los eventos,
  // el cliente pasa a un mes fuera de la ventana, lo ve vacío y concluye que no
  // hay clases. Los dos extremos salen del back (`config`), que es quien decide
  // qué se ha traído; adivinarlos aquí es garantizar que se desalineen.
  //
  // `end` es exclusivo, de ahí el +1 ms sobre el último instante visible
  // (23:59:59.999). `start` es inclusivo y no necesita ajuste.
  //
  // R15 — hacia atrás antes no había tope porque el back traía todo el
  // histórico. Ahora llega hasta el principio del mes pasado: las clases
  // pasadas se consultan, que es media función del calendario, pero no las de
  // hace tres años.
  const validRange = config.visibleUntil || config.visibleFrom
    ? {
      ...(config.visibleFrom ? { start: new Date(config.visibleFrom) } : {}),
      ...(config.visibleUntil
        ? { end: new Date(new Date(config.visibleUntil).getTime() + 1) }
        : {}),
    }
    : undefined;

  const diaYMes = (iso: string) =>
    new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
  const visibleUntilLabel = config.visibleUntil ? diaYMes(config.visibleUntil) : '';
  const visibleFromLabel = config.visibleFrom ? diaYMes(config.visibleFrom) : '';

  // Una recuperación se canjea en CUALQUIER nivel del cliente, no solo en el de
  // la clase cancelada: quien está inscrito en tres niveles y cancela en uno
  // puede recuperar en los tres. Debe coincidir con `rescheduleClass` del back.
  const recoveries = (recoveriesData as Recovery[] | undefined) ?? [];
  const nivelesReservables = new Set<string>([
    ...((levelsData as ClientLevel[] | undefined) ?? []).map((n) => String(n._id)),
    // El nivel de la propia recuperación vale siempre, aunque ya no tenga plaza
    // en él: si no, la recuperación se quedaría atrapada.
    ...recoveries.map((r) => refId(r.levelId as PopulatedRef)).filter(Boolean),
  ]);

  // P1 — si el centro lo permite, el nivel deja de ser un criterio: se puede
  // recuperar en cualquier clase. Único punto que mira el nivel en esta
  // pantalla; el back manda igual (`bookingService.validateRecoveryForClass`),
  // esto solo evita ofrecer una reserva que se iba a rechazar. Campo ausente
  // (back antiguo) = regla restringida de siempre.
  const recoveryAnyLevel = config.recoveryAnyLevel === true;
  const nivelPermitido = (e: ScheduleEvent): boolean =>
    recoveryAnyLevel || nivelesReservables.has(refId(e.nivel_id as PopulatedRef));

  // Recuperación utilizable para una clase concreta, o undefined.
  //
  // El plazo limita cuándo se CELEBRA la clase, no cuándo se reserva: con plazo
  // hasta el 5 de septiembre, una clase del 6 no vale aunque hoy sea día 1. El
  // backend aplica la misma regla en `rescheduleClass`.
  //
  // Las recuperaciones vienen ordenadas por plazo ascendente, así que el primer
  // `find` que cubra la clase es la que antes caduca de entre las que valen:
  // se gasta esa, que es la que más riesgo corre de perderse.
  const recoveryParaClase = (e: ScheduleEvent): Recovery | undefined => {
    const inicio = new Date(toDateTime(e.fecha, e.horaInicio));
    if (inicio <= new Date()) return undefined; // clase ya empezada
    return recoveries.find((r) => new Date(r.rescheduleDeadline) >= inicio);
  };

  // Por qué una clase sale bloqueada. Sin esto, una clase del propio nivel que
  // cae fuera de plazo se ve igual que una de un nivel ajeno, y el cliente no
  // tiene forma de saber que el problema es la fecha.
  const motivoBloqueo = (e: ScheduleEvent): string => {
    if (!recoveries.length) return 'Necesitas una recuperación pendiente para reservar aquí.';
    if (!nivelPermitido(e)) {
      return 'Solo puedes reservar en los niveles en los que estás inscrito.';
    }
    // Van ordenadas por plazo, así que la última marca hasta dónde llega.
    const plazoMasLejano = recoveries[recoveries.length - 1]?.rescheduleDeadline;
    return plazoMasLejano
      ? `Esta clase queda fuera de plazo: puedes recuperar hasta el ${new Date(plazoMasLejano).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}.`
      : 'Esta clase queda fuera del plazo de tu recuperación.';
  };

  // D1 — why this full class can't be joined, or null when it can. Mirrors the
  // backend's joinWaitlist checks so the button only appears when joining will
  // actually work; the backend still decides, and explains, if anything changed.
  const waitlistMode = config.waitlistMode ?? 'off';
  const waitlistCutoffMs = (config.waitlistCutoffMinutes ?? 30) * 60 * 1000;
  const waitlistBlockReason = (e: ScheduleEvent): string | null => {
    if (!nivelPermitido(e)) {
      return 'Solo puedes apuntarte a la lista de espera en los niveles en los que estás inscrito.';
    }
    // `startsAt` comes from the backend in Madrid time; the local fallback is only
    // for an older backend that doesn't send it.
    const startsAt = e.startsAt ? new Date(e.startsAt) : new Date(toDateTime(e.fecha, e.horaInicio));
    if (startsAt.getTime() - Date.now() < waitlistCutoffMs) {
      return 'Empieza demasiado pronto para apuntarse a su lista de espera.';
    }
    if (!recoveryParaClase(e)) {
      return 'Para apuntarte a su lista de espera necesitas una recuperación válida para esta clase.';
    }
    return null;
  };

  const getVariant = (e: ScheduleEvent): ClassVariant => {
    if (e.isEnrolled) return 'enrolled';
    if (e.isFull) return 'full';
    if (!nivelPermitido(e)) return 'locked';
    return recoveryParaClase(e) ? 'bookable' : 'locked';
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Cargando tu calendario...</div>;
  }
  if (isError) {
    return <div className="text-center py-8 text-red-500">No se pudo cargar tu calendario. Inténtalo de nuevo.</div>;
  }

  const calendarEvents = events.map((e) => {
    const variant = getVariant(e);
    const nivel = e.nivel_id as PopulatedRef;
    return {
      id: e._id,
      title: refName(nivel) || 'Clase',
      start: toDateTime(e.fecha, e.horaInicio),
      end: toDateTime(e.fecha, e.horaFin),
      // D1 — a full class the client is queued for gets its own colour, so it
      // doesn't read as "nothing for me here".
      backgroundColor: variant === 'full' && e.onWaitlist ? WAITLIST_BG : variantColor(variant),
      extendedProps: { schedEvent: e, variant },
    };
  });

  const handleEventClick = (info: EventClickArg) => {
    // Un festivo no es una clase: no tiene `schedEvent` y abrirlo dejaría el
    // modal de detalle en blanco.
    const schedEvent = info.event.extendedProps.schedEvent as ScheduleEvent | undefined;
    if (!schedEvent) return;

    setSelected({
      event: schedEvent,
      variant: info.event.extendedProps.variant as ClassVariant,
    });
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    // P5 — los festivos entran en el mismo array de eventos como
    // `display: 'background'`, y FullCalendar llama a este render TAMBIÉN para
    // ellos. No llevan `schedEvent`, así que sin esto se lee `.availableSlots`
    // de `undefined` y revienta el calendario entero.
    const e = eventInfo.event.extendedProps.schedEvent as ScheduleEvent | undefined;
    if (!e) {
      // Un festivo. Sin etiqueta, el alumno ve un día apagado y sin clases y no
      // sabe si es que el centro cierra o si es un fallo.
      if (!eventInfo.event.extendedProps.holiday) return null;
      const descripcion = eventInfo.event.extendedProps.descripcion as string | null;
      return (
        <div className="px-2 pt-1.5 text-sm font-semibold text-slate-700 leading-tight">
          Festivo{descripcion ? ` · ${descripcion}` : ''}
        </div>
      );
    }

    const variant = eventInfo.event.extendedProps.variant as ClassVariant;
    // Ocupación como "ocupadas/total" (p. ej. 2/4).
    const spotsLabel =
      typeof e.availableSlots === 'number' && typeof e.maxClientes === 'number'
        ? `${Math.max(0, e.maxClientes - e.availableSlots)}/${e.maxClientes}`
        : '';
    // Show free spots on every block, including the ones you're enrolled in.
    const chip =
      variant === 'enrolled'
        ? `✓ Inscrito${spotsLabel ? ` · ${spotsLabel}` : ''}`
        : variant === 'bookable'
        ? `+ Reservar${spotsLabel ? ` · ${spotsLabel}` : ''}`
        : variant === 'full'
        ? (e.onWaitlist ? '⏳ En espera' : 'Completa')
        : spotsLabel;
    return (
      <div
        className="h-full w-full rounded-lg overflow-hidden p-1.5"
        style={{
          backgroundColor: eventInfo.backgroundColor,
          color: variant === 'full' && e.onWaitlist ? WAITLIST_TEXT : variantTextColor(variant),
        }}
      >
        <p className="text-[11px] font-semibold">{eventInfo.timeText}</p>
        <b className="text-xs truncate block">{eventInfo.event.title}</b>
        {e.sala && <p className="text-[10px] opacity-80 truncate">Sala {e.sala}</p>}
        {chip && <p className="text-[10px] font-semibold">{chip}</p>}
      </div>
    );
  };

  // Comparación por clave 'YYYY-MM-DD': evita el desfase de un día que sale al
  // mezclar las fechas UTC de la API con los Date locales de FullCalendar.
  const claveDia = (fecha: string): string => fecha.split('T')[0];
  const claveLocal = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // P5 — holidays, painted exactly as the centre's calendar paints them (same
  // `display: 'background'`, same colour), so a holiday reads the same to staff
  // and to the student. Without this the student sees the gap and not the reason,
  // and since P3 the gap is guaranteed: marking a holiday cancels whatever
  // classes that day already had.
  const holidays = data?.holidays ?? [];
  const holidayEvents = holidays.map((h) => ({
    id: `holiday-${claveDia(h.fecha)}`,
    title: h.descripcion || 'Festivo',
    start: claveDia(h.fecha),
    allDay: true,
    display: 'background',
    backgroundColor: HOLIDAY_BG,
    borderColor: HOLIDAY_BG,
    textColor: HOLIDAY_TEXT,
    // Lo lee `renderEventContent` para pintar la etiqueta del día.
    extendedProps: { holiday: true, descripcion: h.descripcion || null },
  }));

  // `end` de FullCalendar es exclusivo.
  const hayClasesEnVista = vistaRango
    ? events.some((e) => {
        const dia = claveDia(e.fecha);
        return dia >= claveLocal(vistaRango.start) && dia < claveLocal(vistaRango.end);
      })
    : true;

  const hoy = claveLocal(new Date());
  const proximaClase = events
    .filter((e) => e.isEnrolled && claveDia(e.fecha) >= hoy)
    .sort((a, b) => (claveDia(a.fecha) + a.horaInicio).localeCompare(claveDia(b.fecha) + b.horaInicio))[0];

  const fechaLarga = (fecha: string): string => {
    const [anio, mes, dia] = claveDia(fecha).split('-').map(Number);
    return new Date(anio, mes - 1, dia).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const selectedRecoveryId = selected ? recoveryParaClase(selected.event)?._id : undefined;
  const cancelInfo = selected
    ? getCancelInfo(selected.event.fecha, selected.event.horaInicio, config.minCancellationHours, config.cancellationHorizonDays)
    : { cancelable: false, earnsRecovery: false, message: '' };

  const handleCancel = () => {
    if (!selected) return;
    cancelClass.mutate(selected.event._id, {
      // El servidor es quien decide si hubo recuperación (su reloj manda), así
      // que el aviso sale de la respuesta, no del cálculo local.
      onSuccess: (res: { recoveryGranted?: boolean } | undefined) => {
        if (res?.recoveryGranted === false) {
          toast.info('Clase cancelada. Esta cancelación no genera recuperación.');
        } else {
          toast.success('Clase cancelada. Ahora puedes recuperarla.');
        }
        setSelected(null);
      },
      onError: () => toast.error('No se pudo cancelar la clase. Inténtalo de nuevo.'),
    });
  };

  const handleBook = () => {
    if (!selected || !selectedRecoveryId) return;
    reschedule.mutate(
      { recoveryId: selectedRecoveryId, newEventId: selected.event._id },
      { onSuccess: () => setSelected(null) }
    );
  };

  // D1 — waitlist actions. Messages (success and every refusal reason) come from
  // the hooks and the server.
  const handleJoinWaitlist = () => {
    if (!selected) return;
    joinWaitlist.mutate(selected.event._id, { onSuccess: () => setSelected(null) });
  };

  const handleLeaveWaitlist = () => {
    const entryId = selected?.event.waitlistEntryId;
    if (!entryId) return;
    leaveWaitlist.mutate(entryId, { onSuccess: () => setSelected(null) });
  };

  const selectedWaitlistBlock = selected && selected.variant === 'full' && waitlistMode !== 'off'
    ? waitlistBlockReason(selected.event)
    : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold">Mi Calendario</h2>
        <span className="text-xs text-gray-500">Toca una clase para ver los detalles.</span>
      </div>

      {/* El calendario se corta a propósito: explicarlo evita que el cliente
          crea que se han dejado de programar clases. */}
      {visibleUntilLabel && (
        <p className="text-xs text-gray-500 mb-3">
          Se muestran las clases{' '}
          {visibleFromLabel && <>desde el <strong>{visibleFromLabel}</strong>{' '}</>}
          hasta el <strong>{visibleUntilLabel}</strong>. Las siguientes
          aparecerán más adelante.
        </p>
      )}

      {/* Color key: state, not level */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: '#bbf7d0' }} /> Inscrito
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: '#bfdbfe' }} /> Recuperación disponible
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: '#fecaca' }} /> Completa
        </span>
        {waitlistMode !== 'off' && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: WAITLIST_BG }} /> En lista de espera
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: '#e5e7eb' }} /> Otras clases
        </span>
      </div>

      {events.length === 0 ? (
        <p className="text-gray-500 text-sm mb-4">Aún no hay clases programadas.</p>
      ) : (
        !isMobile && !hayClasesEnVista && (
          <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p>
              No hay clases {vistaRango?.titulo ? <>en <strong>{vistaRango.titulo}</strong></> : 'en estas fechas'}.
            </p>
            {proximaClase && (
              <p className="mt-1">
                Tu próxima clase es el <strong>{fechaLarga(proximaClase.fecha)}</strong> a las{' '}
                <strong>{proximaClase.horaInicio}</strong>.
              </p>
            )}
          </div>
        )
      )}

      {isMobile ? (
        <MobileDayAgenda
          events={events}
          holidays={holidays}
          getVariant={getVariant}
          onSelect={(event, variant) => setSelected({ event, variant })}
        />
      ) : (
        <BaseCalendar
          events={[...calendarEvents, ...holidayEvents]}
          eventContent={renderEventContent}
          onEventClick={handleEventClick}
          validRange={validRange}
          // Devolver el estado ANTERIOR cuando el rango no ha cambiado es
          // obligatorio, no una optimización: FullCalendar dispara datesSet en
          // cada render, y guardar siempre un objeto nuevo provocaba
          // render → datesSet → setState → render… (React #185).
          onDatesSet={(arg) =>
            setVistaRango((prev) =>
              prev
                && prev.start.getTime() === arg.start.getTime()
                && prev.end.getTime() === arg.end.getTime()
                && prev.titulo === arg.view.title
                ? prev
                : { start: arg.start, end: arg.end, titulo: arg.view.title }
            )
          }
        />
      )}

      {selected && (
        <ClientClassDetailModal
          nivelName={refName(selected.event.nivel_id as PopulatedRef) || 'Clase'}
          instructorName={refName(selected.event.instructor_id as PopulatedRef)}
          sala={selected.event.sala}
          startsAt={new Date(toDateTime(selected.event.fecha, selected.event.horaInicio))}
          endLabel={selected.event.horaFin}
          variant={selected.variant}
          availableSlots={selected.event.availableSlots}
          maxClientes={selected.event.maxClientes}
          recoveryDeadline={selected.event.recoveryDeadline}
          cancelable={cancelInfo.cancelable}
          earnsRecovery={cancelInfo.earnsRecovery}
          cancelNoticeMessage={cancelInfo.message}
          isCancelling={cancelClass.isPending}
          onCancelClass={handleCancel}
          isBooking={reschedule.isPending}
          onBook={handleBook}
          lockedReason={selected.variant === 'locked' ? motivoBloqueo(selected.event) : undefined}
          waitlistMode={waitlistMode}
          onWaitlist={Boolean(selected.event.onWaitlist)}
          canJoinWaitlist={selected.variant === 'full' && waitlistMode !== 'off' && selectedWaitlistBlock === null}
          waitlistBlockedReason={selectedWaitlistBlock ?? undefined}
          isJoiningWaitlist={joinWaitlist.isPending}
          onJoinWaitlist={handleJoinWaitlist}
          isLeavingWaitlist={leaveWaitlist.isPending}
          onLeaveWaitlist={handleLeaveWaitlist}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default ClientCalendar;
